import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { fetchReportData } from '../../functions/fetchAnalyticsData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './AgentsLedger.css';

function AgentsLedger() {
  const [agentsData, setAgentsData] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalAgents: 0,
    totalSales: 0,
    totalCommission: 0,
    topAgent: '',
    topAgentSales: 0,
    averageSalesPerAgent: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAgent, setSelectedAgent] = useState('all');

  const fetchAgentsLedgerData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchReportData('agents-ledger', { period: selectedPeriod, date: selectedDate, agent: selectedAgent });
      
      if (data) {
        const agentsData = data.agentsData || [];
        const performanceData = data.performanceData || [];
        const commissionData = data.commissionData || [];
        
        setAgentsData(agentsData);
        setPerformanceData(performanceData);
        setCommissionData(commissionData);
        
        // Calculate summary statistics
        const totalSales = agentsData.reduce((sum, agent) => sum + (agent.sales || 0), 0);
        const totalCommission = agentsData.reduce((sum, agent) => sum + (agent.commission || 0), 0);
        const totalAgents = agentsData.length;
        const averageSalesPerAgent = totalAgents > 0 ? totalSales / totalAgents : 0;
        
        const topAgentData = agentsData.reduce((max, agent) => 
          (agent.sales || 0) > (max.sales || 0) ? agent : max, agentsData[0] || {});
        
        setSummaryData({
          totalAgents,
          totalSales,
          totalCommission,
          topAgent: topAgentData.name || '',
          topAgentSales: topAgentData.sales || 0,
          averageSalesPerAgent
        });
      }
    } catch (error) {
      console.error('Error fetching agents ledger data:', error);
      setError('Failed to load agents ledger data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentsLedgerData();
  }, [selectedPeriod, selectedDate, selectedAgent]);

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleAgentChange = (e) => {
    setSelectedAgent(e.target.value);
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 90) return '#27ae60';
    if (performance >= 75) return '#f39c12';
    if (performance >= 60) return '#e67e22';
    return '#e74c3c';
  };

  const COLORS = ['#d90429', '#3498db', '#27ae60', '#f39c12', '#9b59b6', '#e74c3c', '#1abc9c', '#34495e'];

  if (isLoading) {
    return (
      <Layout>
        <Header title="Agents Ledger Report" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading agents ledger report...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Agents Ledger Report" />
      <div className="agents-ledger-container">
        
        {/* Controls */}
        <div className="row mb-4">
          <div className="col-md-2">
            <div className="form-group">
              <label htmlFor="periodSelect">Select Period:</label>
              <select
                id="periodSelect"
                className="form-control"
                value={selectedPeriod}
                onChange={handlePeriodChange}
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="dateSelect">Select Date:</label>
              <input
                type="date"
                id="dateSelect"
                className="form-control"
                value={selectedDate}
                onChange={handleDateChange}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="agentSelect">Select Agent:</label>
              <select
                id="agentSelect"
                className="form-control"
                value={selectedAgent}
                onChange={handleAgentChange}
              >
                <option value="all">All Agents</option>
                {agentsData.map((agent, index) => (
                  <option key={index} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4 text-end">
            <button 
              className="btn btn-outline-primary me-2"
              onClick={fetchAgentsLedgerData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
            <button className="btn btn-success">
              <i className="fas fa-download me-2"></i>
              Export
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="summary-card total-agents">
              <div className="card-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalAgents}</h3>
                <p>Total Agents</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="summary-card total-sales">
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.totalSales.toLocaleString()}</h3>
                <p>Total Sales</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="summary-card total-commission">
              <div className="card-icon">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.totalCommission.toLocaleString()}</h3>
                <p>Total Commission</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="summary-card top-agent">
              <div className="card-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.topAgent}</h3>
                <p>Top Agent (฿{summaryData.topAgentSales.toLocaleString()})</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
            <div className="summary-card average-sales">
              <div className="card-icon">
                <i className="fas fa-calculator"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.averageSalesPerAgent.toLocaleString()}</h3>
                <p>Average per Agent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row">
          {/* Sales Performance Chart */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Agent Sales Performance</h5>
              </div>
              <div className="panel-content">
                {agentsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={agentsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="name" 
                        stroke="#666" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                      />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          name === 'sales' ? `฿${value.toLocaleString()}` : value,
                          name === 'sales' ? 'Sales' : 'Orders'
                        ]}
                      />
                      <Bar dataKey="sales">
                        {agentsData.map((entry, index) => (
                          <Cell key={`sales-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                      <Bar dataKey="orders">
                        {agentsData.map((entry, index) => (
                          <Cell key={`orders-${index}`} fill={COLORS[(index + 4) % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-bar"></i>
                    <p>No agent performance data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Commission Distribution */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Commission Distribution</h5>
              </div>
              <div className="panel-content">
                {commissionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={commissionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="commission"
                      >
                        {commissionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`฿${value.toLocaleString()}`, 'Commission']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-pie"></i>
                    <p>No commission data available</p>
                  </div>
                )}
                
                {/* Commission Legend */}
                {commissionData.length > 0 && (
                  <div className="chart-legend">
                    {commissionData.map((agent, index) => (
                      <div key={index} className="legend-item">
                        <div 
                          className="legend-color" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="legend-text">{agent.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Trend */}
        {performanceData.length > 0 && (
          <div className="row">
            <div className="col-12 mb-4">
              <div className="chart-panel">
                <div className="panel-header">
                  <h5>Performance Trend Over Time</h5>
                </div>
                <div className="panel-content">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="date" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                      />
                      {agentsData.slice(0, 5).map((agent, index) => (
                        <Line 
                          key={agent.name}
                          type="monotone" 
                          dataKey={agent.name} 
                          stroke={COLORS[index % COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Agents Table */}
        <div className="row">
          <div className="col-12">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Detailed Agent Performance Report</h5>
              </div>
              <div className="panel-content">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Sales Amount</th>
                        <th>Orders</th>
                        <th>Commission</th>
                        <th>Commission Rate</th>
                        <th>Avg Order Value</th>
                        <th>Performance</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentsData.length > 0 ? (
                        agentsData
                          .sort((a, b) => (b.sales || 0) - (a.sales || 0))
                          .map((agent, index) => {
                            const avgOrderValue = agent.orders > 0 ? agent.sales / agent.orders : 0;
                            const commissionRate = agent.sales > 0 ? (agent.commission / agent.sales * 100) : 0;
                            const performance = agent.performance || 0;
                            
                            return (
                              <tr key={index}>
                                <td className="font-weight-bold">
                                  <div className="d-flex align-items-center">
                                    <div className="agent-avatar">
                                      {agent.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="ms-2">
                                      <div>{agent.name}</div>
                                      <small className="text-muted">{agent.email}</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-success font-weight-bold">
                                  ฿{agent.sales.toLocaleString()}
                                </td>
                                <td>
                                  <span className="badge bg-primary">{agent.orders || 0}</span>
                                </td>
                                <td className="text-info font-weight-bold">
                                  ฿{agent.commission.toLocaleString()}
                                </td>
                                <td>{commissionRate.toFixed(1)}%</td>
                                <td>฿{avgOrderValue.toFixed(2)}</td>
                                <td>
                                  <div className="performance-indicator">
                                    <div 
                                      className="performance-bar"
                                      style={{
                                        width: `${Math.min(performance, 100)}%`,
                                        backgroundColor: getPerformanceColor(performance)
                                      }}
                                    ></div>
                                    <span className="performance-text">{performance}%</span>
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-badge ${agent.status || 'active'}`}>
                                    {agent.status || 'Active'}
                                  </span>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="btn btn-sm btn-outline-primary me-1">
                                      <i className="fas fa-eye"></i>
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary">
                                      <i className="fas fa-edit"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center text-muted">
                            No agent data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default AgentsLedger;
